import isEqual from "lodash/isEqual";
import { MultiActivityFormStoreProvider } from "PFApp/stores/activity_form/multi_roles_form";
import { Id } from "PFCore/types";
import { PropsWithChildren, useState } from "react";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { ManageRolesStoreType, UnsavedEditStatus } from "./manage_roles_store.types";
import { isUnresolvedEdit } from "./manage_roles_store.utils";
import { ManageRolesStoreContext } from "./manage_roles_store_context";
import { useAvailableManageRolesAdditionalColumns } from "./use_available_manage_roles_additional_columns";

type ManageRolesStoreProviderProps = PropsWithChildren<{
  initialActivityIds?: Id[];
  initialNewActivityIds?: Id[];
}>;

export const ManageRolesStoreProvider = ({
  children,
  initialActivityIds = [],
  initialNewActivityIds = []
}: ManageRolesStoreProviderProps) => {
  const availableColumns = useAvailableManageRolesAdditionalColumns();

  const [store] = useState(
    createStore<ManageRolesStoreType>()(
      persist(
        immer((set, get) => ({
          currentPage: 1,
          unsavedEdits: {},
          activityIds: initialActivityIds,
          newActivityIds: initialNewActivityIds,
          newDraftRoles: [],
          availableColumns,
          selectedColumns: ["status"],
          actions: {
            setCurrentPage: (page) =>
              set((state) => {
                state.currentPage = page;
              }),
            addUnsavedEdit: (
              roleId: Id,
              fieldName: string,
              value: unknown,
              previousValue: unknown,
              page: number,
              status: UnsavedEditStatus = "unsaved"
            ) =>
              set((state) => {
                const key = `${roleId}.${fieldName}`;
                const existing = state.unsavedEdits[key];
                if (existing && existing.status === "unsaved" && isEqual(existing.value, value)) {
                  return;
                }
                // If an inflight or failed entry exists, we overwrite it with status "unsaved" so it will be
                // retried. previousValue is preserved from the original edit so that on failure we still
                // know the correct pre-edit server value.
                state.unsavedEdits[key] = {
                  roleId,
                  fieldName,
                  value,
                  previousValue: existing ? existing.previousValue : previousValue,
                  status,
                  isError: status === "failed" ? true : (existing?.isError ?? false),
                  page
                };
              }),
            markInflight: (keys: string[]) =>
              set((state) => {
                keys.forEach((key) => {
                  if (state.unsavedEdits[key]) {
                    state.unsavedEdits[key].status = "inflight";
                  }
                });
              }),
            markFailed: (keys: string[]) =>
              set((state) => {
                keys.forEach((key) => {
                  if (state.unsavedEdits[key]) {
                    state.unsavedEdits[key].status = "failed";
                    state.unsavedEdits[key].isError = false;
                  }
                });
              }),
            markKeysAsErrored: (keys: string[]) =>
              set((state) => {
                keys.forEach((key) => {
                  if (state.unsavedEdits[key]) {
                    state.unsavedEdits[key].isError = true;
                  }
                });
              }),
            clearEdits: (keys: string[]) =>
              set((state) => {
                keys.forEach((key) => {
                  delete state.unsavedEdits[key];
                });
              }),
            remapRoleEdits: (oldRoleId: Id, newRoleId: Id) =>
              set((state) => {
                Object.entries(state.unsavedEdits).forEach(([key, edit]) => {
                  if (edit.roleId !== oldRoleId) {
                    return;
                  }
                  delete state.unsavedEdits[key];
                  if (edit.status === "inflight") {
                    return;
                  }
                  state.unsavedEdits[`${newRoleId}.${edit.fieldName}`] = { ...edit, roleId: newRoleId };
                });
              }),
            changeFailedEditsToUnsaved: (roleId: Id) =>
              set((state) => {
                Object.values(state.unsavedEdits).forEach((edit) => {
                  if (edit.roleId === roleId && edit.status === "failed") {
                    edit.status = "unsaved";
                  }
                });
              }),
            // Intentionally an action (not a selector) — reads state imperatively via get() without
            // subscribing, so callers don't re-render on every unsavedEdits mutation.
            hasUnsavedEdits: () => {
              const state = get();
              return Object.values(state.unsavedEdits).some((edit) =>
                isUnresolvedEdit(edit, state.newDraftRoles)
              );
            },
            addNewActivityIds: (ids: Id[]) =>
              set((state) => {
                ids.forEach((id) => {
                  if (!state.newActivityIds.includes(id)) {
                    state.newActivityIds.push(id);
                  }
                });
              }),
            addNewDraftRole: (role) =>
              set((state) => {
                state.newDraftRoles.push(role);
              }),
            removeNewDraftRole: (uniqueId: string) =>
              set((state) => {
                state.newDraftRoles = state.newDraftRoles.filter((role) => role.uniqueId !== uniqueId);
              }),
            setSelectedColumns: (columnNames) =>
              set((state) => {
                state.selectedColumns = columnNames;
              }),
            setAvailableColumns: (columns) =>
              set((state) => {
                state.availableColumns = columns;
              })
          }
        })),
        {
          name: "manage-roles-store",
          partialize: (state) => ({ selectedColumns: state.selectedColumns })
        }
      )
    )
  );

  return (
    <ManageRolesStoreContext.Provider value={store}>
      <MultiActivityFormStoreProvider>{children}</MultiActivityFormStoreProvider>
    </ManageRolesStoreContext.Provider>
  );
};
